import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { AppUpdatesApiService } from "./app-updates-api.service";
import { DialogUpdateCompleteComponent } from "../common/dialog-refresh/dialog-update-complete.component";

/**
 * Shows the "you're up to date! / what's new" modal (FLIP-79) exactly once per app
 * version per user, on the first session actually running that version.
 *
 * Covers all three arrival routes the ticket lists - Update now, a snooze re-show, and
 * a fresh open with no prior prompt - from one call site: the first two both end in a
 * real `location.reload()`, which is indistinguishable at boot time from the third, so
 * there's nothing route-specific to hook.
 *
 * "Seen" is tracked server-side (FLIP-81's UserAppUpdateStatuses table, via
 * GET /api/app-updates/latest / POST /api/app-updates/mark-seen), not localStorage - the
 * ticket rules that out as unreliable across a mobile reinstall. GetLatestUnseenAsync
 * already excludes anything this user has been shown, so there's no client-side flag-key
 * bookkeeping needed here at all: a non-null response IS "show it".
 */
@Injectable({ providedIn: "root" })
export class UpdateCompleteModalService {
  constructor(private dialog: MatDialog, private appUpdatesApiService: AppUpdatesApiService) {}

  /**
   * Fetches the latest unseen live announcement and shows the modal if there is one.
   */
  checkAndMaybeShow(): void {
    this.appUpdatesApiService.getLatest().subscribe({
      next: (response) => {
        const announcement = response?.responseData;
        if (!announcement) {
          // Nothing new for this user - clean no-op.
          return;
        }
        this.show(
          announcement.version,
          announcement.newFeatures ?? [],
          announcement.bugFixes ?? []
        );
      },
      // Non-critical: skip showing the modal this time rather than risk it never
      // showing at all if this particular fetch fails.
      error: () => {},
    });
  }

  private show(version: string, newFeatures: string[], bugFixes: string[]): void {
    this.dialog.open(DialogUpdateCompleteComponent, {
      panelClass: "base-modal-panel",
      backdropClass: "base-modal-backdrop",
      data: { version, newFeatures, bugFixes },
    });

    // Marked seen as soon as the modal is actually shown (matches
    // CommunityPromoModalService's existing "seen at open time" convention) - the DoD
    // asks for "once the modal has actually been shown", not once it's been dismissed.
    this.appUpdatesApiService.markSeen(version).subscribe();
  }
}
